import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { IS_RECMAPPER } from '../product';

/**
 * Free-trial nudges (MarketMapr): the slim trial bar, and one "activate your
 * plan" pop-up for the moments a trialist meets a trial limit —
 *   export / monitoring / job_watches — paid-plan features (server answers 402)
 *   import  — an import added the first 1,000 of a larger search
 *   first   — after their first import (once)
 *
 * requestTrialUpgrade(detail) opens it from anywhere; promptAfterReload()
 * keeps it across the full-page navigation an import ends with.
 */
export type TrialPrompt = { reason: 'export' | 'import' | 'first' | 'monitoring' | 'job_watches' | 'discovery' | 'keep_growing'; to?: number; of?: number; what?: string };

const EVENT = 'trial-upgrade';

// Exports built in the browser (map spreadsheet, campaign CSV, monitoring
// reports) never reach the server's trial check — they call this first.
let onTrialNow = false;
export function blockedForTrial(): boolean {
  if (!onTrialNow) return false;
  requestTrialUpgrade({ reason: 'export' });
  return true;
}
const STORE = 'trialPrompt';

export function requestTrialUpgrade(detail: TrialPrompt) {
  window.dispatchEvent(new CustomEvent(EVENT, { detail }));
}
export function promptAfterReload(detail: TrialPrompt) {
  try { sessionStorage.setItem(STORE, JSON.stringify(detail)); } catch { /* storage off — skip the prompt */ }
}

const daysLeft = (endsAt: string | null | undefined) => {
  if (!endsAt) return null;
  const ms = new Date(endsAt).getTime() - Date.now();
  return Number.isFinite(ms) ? Math.max(0, Math.ceil(ms / 86400000)) : null;
};

export const TrialBar: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const trial = (user as any)?.trial;
  if (IS_RECMAPPER || !trial) return null;
  const d = daysLeft(trial.endsAt);
  return (
    <div className="bg-teal-700 text-white text-xs">
      <div className="px-6 py-1.5 flex items-center gap-3 flex-wrap">
        <span className="font-semibold">Free trial{d !== null ? ` · ${d} day${d === 1 ? '' : 's'} left` : ''}</span>
        <span className="text-teal-100">
          Imports add up to {(trial.importLimit || 1000).toLocaleString()} records each; exports and contact credits start with your paid plan.
        </span>
        <button
          onClick={() => navigate('/settings?tab=subscription')}
          className="ml-auto bg-white text-teal-800 font-semibold rounded px-2.5 py-0.5 hover:bg-teal-50"
        >
          Activate now
        </button>
      </div>
    </div>
  );
};

export const TrialUpgradeModal: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState<TrialPrompt | null>(null);
  const onTrial = !IS_RECMAPPER && !!(user as any)?.trial;

  useEffect(() => {
    onTrialNow = onTrial;
    if (!onTrial) return;
    const open = (e: Event) => setPrompt((e as CustomEvent<TrialPrompt>).detail);
    window.addEventListener(EVENT, open);
    try {
      const saved = sessionStorage.getItem(STORE);
      if (saved) { sessionStorage.removeItem(STORE); setPrompt(JSON.parse(saved)); }
    } catch { /* ignore */ }
    // Any request the server refuses as a trial export (402 trialBlocked)
    // opens the prompt — every export / push button, present and future.
    const orig = window.fetch;
    window.fetch = async (...args: Parameters<typeof fetch>) => {
      const r = await orig(...args);
      if (r.status === 402) {
        r.clone().json().then((b) => {
          if (b && b.trialBlocked) requestTrialUpgrade({ reason: ['monitoring', 'job_watches', 'discovery', 'keep_growing'].includes(b.feature) ? b.feature : 'export' });
        }).catch(() => {});
      }
      return r;
    };
    return () => { window.removeEventListener(EVENT, open); window.fetch = orig; };
  }, [onTrial]);

  if (!onTrial || !prompt) return null;
  const what = prompt.what || 'people';
  const title = prompt.reason === 'export'
    ? 'Exporting is part of your paid plan'
    : prompt.reason === 'monitoring'
      ? 'Monitoring starts with your paid plan'
      : prompt.reason === 'job_watches'
        ? 'Job Watches start with your paid plan'
      : prompt.reason === 'keep_growing'
        ? 'Keep growing starts with your paid plan'
        : prompt.reason === 'discovery'
          ? 'You\'ve used your trial\'s auto-discovery'
        : prompt.reason === 'import'
      ? `Your trial added the first ${(prompt.to || 1000).toLocaleString()} ${what}`
      : 'Your first map is ready';
  const body = prompt.reason === 'export'
    ? 'During the free trial you can search, map and explore everything — exports and pushes to your other tools start when you activate your plan.'
    : prompt.reason === 'monitoring'
      ? 'Monitoring keeps watching your mapped people for job moves, promotions and new roles. It switches on when you activate your plan — everything you map during the trial is ready for it.'
      : prompt.reason === 'job_watches'
        ? 'Job Watches track the companies you map for new vacancies. They start when you activate your plan.'
      : prompt.reason === 'keep_growing'
        ? 'Keep growing re-runs this project\'s search every week and adds the people who newly match. It starts when you activate your plan.'
        : prompt.reason === 'discovery'
          ? 'The free trial includes hiring manager auto-discovery for 25 companies. Activate your plan to run it across every company you map — every plan includes free discoveries each month (Starter 1,000 · Growth 3,000 · Scale 10,000 companies), then 1 credit per company.'
        : prompt.reason === 'import'
      ? `This search found ${(prompt.of || 0).toLocaleString()} ${what}. On the free trial each import adds the first ${(prompt.to || 1000).toLocaleString()} — activate your plan to add all of them, export, and get your contact credits.`
      : 'You\'re seeing your market mapped. On the free trial each import adds up to 1,000 records — activate your plan for full imports, exports and your contact credits.';

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4" onClick={() => setPrompt(null)}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 leading-relaxed mb-5">{body}</p>
        <div className="flex gap-2 justify-end">
          <button onClick={() => setPrompt(null)} className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800">
            Keep exploring
          </button>
          <button
            onClick={() => { setPrompt(null); navigate('/settings?tab=subscription'); }}
            className="px-4 py-2 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700"
          >
            Activate my plan
          </button>
        </div>
      </div>
    </div>
  );
};


// Viewer seats: the server refuses exports / pushes / credit spending with
// 403 viewerBlocked (middleware/viewer.js). Every such button, present and
// future, gets this one notice instead of a silent failure.
export const ViewerBlockedNotice: React.FC = () => {
  const { user } = useAuth();
  const [msg, setMsg] = useState<string | null>(null);
  const viewer = !!(user as any)?.teamId && (user as any)?.teamRole === 'viewer';
  useEffect(() => {
    if (!viewer) return;
    const orig = window.fetch;
    window.fetch = async (...args: Parameters<typeof fetch>) => {
      const r = await orig(...args);
      if (r.status === 403) {
        r.clone().json().then((b) => { if (b && b.viewerBlocked) setMsg(b.error || 'Viewers can look but not take data out or spend credits.'); }).catch(() => {});
      }
      return r;
    };
    return () => { window.fetch = orig; };
  }, [viewer]);
  if (!viewer || !msg) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4" onClick={() => setMsg(null)}>
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-gray-900">Viewer seat</h3>
        <p className="text-sm text-gray-600 mt-2">{msg}</p>
        <p className="text-xs text-gray-500 mt-2">You can search, map and build projects, and look at everything in them. Exporting, pushing to other tools and anything that spends credits needs a member seat.</p>
        <div className="flex justify-end mt-4">
          <button className="px-4 py-1.5 rounded-md text-sm font-medium bg-gray-900 text-white hover:bg-gray-800" onClick={() => setMsg(null)}>OK</button>
        </div>
      </div>
    </div>
  );
};
